import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { allTours } from '../../mocks/tours';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TourDetailHero from './components/TourDetailHero';
import ProgramSection from './components/ProgramSection';
import IncludesSection from './components/IncludesSection';
import ReservationForm from './components/ReservationForm';
import TourCard from '../../components/base/TourCard';

export default function TurDetay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tour = allTours.find((t) => t.id === id);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    if (!tour) {
      navigate('/');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, tour, navigate]);

  if (!tour) return null;

  const categoryLabel = tour.category === 'gunubirlik' ? 'Günübirlik' : tour.category === 'konaklamali' ? 'Konaklamalı' : tour.category === 'ulasimli' ? 'Ulaşımlı Tatil' : 'Yurt Dışı';
  const priceFormatted = tour.currency === 'TL'
    ? `₺${tour.price.toLocaleString('tr-TR')}`
    : `€${tour.price}`;
  const pageTitle = `${tour.name} | Ankara Çıkışlı ${categoryLabel} Turu - Ankara Travelers`;
  const pageDescription = `${tour.name} - Ankara çıkışlı ${categoryLabel.toLowerCase()} turu. ${tour.duration} ${tour.durationUnit} süren bu tur ${priceFormatted} kişi başı fiyatla sunulmaktadır. ${tour.description?.slice(0, 100) ?? ''}`;

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    '@id': `${siteUrl}/tur/${tour.id}`,
    url: `${siteUrl}/tur/${tour.id}`,
    name: tour.name,
    description: tour.description,
    inLanguage: 'tr-TR',
    image: tour.image,
    touristType: 'Group tour',
    itinerary: {
      '@type': 'ItemList',
      name: `${tour.name} Programı`,
    },
    provider: {
      '@type': 'TravelAgency',
      name: 'Ankara Travelers',
      url: siteUrl,
      telephone: '+905400015806',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Ankara',
        addressCountry: 'TR',
      },
    },
    offers: {
      '@type': 'Offer',
      price: tour.price,
      priceCurrency: tour.currency === 'TL' ? 'TRY' : 'EUR',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/tur/${tour.id}`,
    },
    duration: `P${tour.duration}D`,
  };

  const relatedTours = allTours
    .filter((t) => t.category === tour.category && t.id !== tour.id)
    .slice(0, 3);

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription.slice(0, 160)} />
      <meta name="keywords" content={`${tour.name}, Ankara çıkışlı tur, ${categoryLabel.toLowerCase()} tur Ankara, Ankara Travelers`} />
      <link rel="canonical" href={`${siteUrl}/tur/${tour.id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription.slice(0, 160)} />
      <meta property="og:url" content={`${siteUrl}/tur/${tour.id}`} />
      <meta property="og:image" content={tour.image} />
      <meta property="og:type" content="product" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription.slice(0, 160)} />
      <meta name="twitter:image" content={tour.image} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      <Navbar />
      <main className="min-h-screen bg-gray-50">
        <TourDetailHero tour={tour} />

        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Quick Info Bar */}
          <div className="bg-white rounded-2xl border border-gray-100 px-6 py-4 mb-8 flex flex-wrap gap-6 items-center">
            {[
              { icon: 'ri-map-pin-2-line', label: 'Kalkış', value: 'Ankara' },
              { icon: 'ri-calendar-line', label: 'Süre', value: `${tour.duration} ${tour.durationUnit}` },
              { icon: 'ri-group-line', label: 'Tur Tipi', value: 'Grup Turu' },
              { icon: 'ri-translate-2', label: 'Rehber Dili', value: 'Türkçe' },
              { icon: 'ri-star-line', label: 'Kategori', value: tour.category === 'gunubirlik' ? 'Günübirlik' : tour.category === 'konaklamali' ? 'Konaklamalı' : tour.category === 'ulasimli' ? 'Ulaşımlı Tatil' : 'Yurt Dışı' },
            ].map((info) => (
              <div key={info.label} className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
                  <i className={`${info.icon} text-orange-500 text-sm`} />
                </div>
                <div>
                  <p className="text-xs text-gray-400">{info.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{info.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-xl">
                    <i className="ri-information-line text-orange-500 text-lg" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Tur Hakkında</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">{tour.description}</p>

                <div className="mt-5 p-4 bg-orange-50 rounded-xl border border-orange-100">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-orange-500 text-sm" />
                    </div>
                    <p className="text-sm text-orange-700">
                      Bu tur Ankara çıkışlı olup tüm transferler klimalı araçlarla sağlanmaktadır. Tur ücretine KDV dahildir.
                    </p>
                  </div>
                </div>
              </div>

              {/* Program */}
              <ProgramSection tour={tour} />

              {/* Includes */}
              <IncludesSection tour={tour} />

              {/* Notes */}
              <div className="bg-white rounded-2xl p-7 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-xl">
                    <i className="ri-file-text-line text-orange-500 text-lg" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">Önemli Bilgiler</h2>
                </div>
                <ul className="space-y-3">
                  {[
                    'Tur saatleri ve programı hava koşullarına göre değişiklik gösterebilir.',
                    'Çocuklar için özel fiyat uygulanmaktadır, lütfen iletişime geçin.',
                    'Rezervasyon için %30 ön ödeme alınmaktadır.',
                    'İptal koşulları için lütfen müşteri hizmetleri ile iletişime geçin.',
                    'Tur günü yanınızda kimlik belgesi bulundurmanız zorunludur.',
                  ].map((note) => (
                    <li key={note} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-orange-400 text-xs" />
                      </div>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1">
              <ReservationForm tour={tour} />
            </div>
          </div>

          {/* Related Tours */}
          {relatedTours.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Benzer Turlar</h2>
                <Link
                  to={`/${tour.category === 'gunubirlik' ? 'gunubirlik-turlar' : tour.category === 'konaklamali' ? 'konaklamali-turlar' : tour.category === 'ulasimli' ? 'ulasimli-tatil' : 'yurtdisi-turlar'}`}
                  className="text-sm text-orange-500 font-medium hover:text-orange-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  Tümünü Gör
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedTours.map((related) => (
                  <TourCard key={related.id} tour={related} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
